/**
 * Login Component - Glassmorphism Design with BizHub Branding
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useTier } from '../contexts/TierContext';
import { LOGIN_TEXT } from '../constants/auth';

const LoginGlass = () => {
  const navigate = useNavigate();
  const { login } = useTier();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // Check if user just registered or verified email
    if (searchParams.get('registered') === 'true' || searchParams.get('verified') === 'true') {
      setShowSuccess(true);
      const timer = setTimeout(() => setShowSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.login(formData);
      const { access_token, user } = response.data;
      
      login(access_token, user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Animated Gradient Background - Optimized for Logo Colors */}
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 via-teal-500 to-blue-900 animate-gradient" style={{ background: 'linear-gradient(135deg, #22d3ee 0%, #14b8a6 20%, #0891b2 40%, #0e7490 60%, #1e3a8a 80%, #1e293b 100%)' }}></div>
      
      {/* Animated Background Shapes - Adjusted Colors */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-72 h-72 bg-cyan-300/30 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-float-delayed"></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-teal-400/25 rounded-full blur-3xl animate-pulse"></div>
      </div>

      {/* Login Container */}
      <div className="relative z-10 w-full max-w-md px-6">
        {/* Glassmorphism Card - Enhanced with Logo Colors */}
        <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 via-cyan-50/5 to-blue-50/10 rounded-3xl border border-white/20 shadow-2xl p-8">
          {/* Logo & Header */}
          <div className="text-center mb-8">
            <div className="inline-block mb-6">
              <img 
                src="/bizhub-logo-v2.png" 
                alt="BizHub Logo" 
                className="h-24 w-auto mx-auto mb-2"
              />
              <div className="text-sm text-white/80 font-light tracking-wider">
                {LOGIN_TEXT.tagline}
              </div>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">
              {LOGIN_TEXT.heading}
            </h2>
            <p className="text-white/70 text-sm">
              {LOGIN_TEXT.subheading}
            </p>
          </div>

          {/* Success Message */}
          {showSuccess && (
            <div className="mb-6 backdrop-blur-lg bg-green-500/20 border border-green-400/30 rounded-2xl p-4 animate-fade-in">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-green-100 text-sm font-medium">
                  {searchParams.get('verified') === 'true' 
                    ? LOGIN_TEXT.verifiedSuccess
                    : LOGIN_TEXT.registeredSuccess}
                </span>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 backdrop-blur-lg bg-red-500/20 border border-red-400/30 rounded-2xl p-4 animate-fade-in">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-red-100 text-sm font-medium">{error}</span>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Input */}
            <div className="relative">
              <div className="flex items-center backdrop-blur-lg bg-white/5 border border-white/20 rounded-2xl overflow-hidden focus-within:border-cyan-400/50 transition-all duration-300">
                <div className="pl-4 text-white/50">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-4 bg-transparent text-white placeholder-white/50 outline-none"
                  placeholder={LOGIN_TEXT.emailPlaceholder}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="relative">
              <div className="flex items-center backdrop-blur-lg bg-white/5 border border-white/20 rounded-2xl overflow-hidden focus-within:border-cyan-400/50 transition-all duration-300">
                <div className="pl-4 text-white/50">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  className="w-full px-4 py-4 bg-transparent text-white placeholder-white/50 outline-none"
                  placeholder={LOGIN_TEXT.passwordPlaceholder}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="pr-4 text-white/50 hover:text-white/80 transition-colors"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Remember & Forgot Password */}
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center text-white/80 hover:text-white cursor-pointer group">
                <input 
                  type="checkbox" 
                  className="mr-2 w-4 h-4 rounded border-white/30 bg-white/10 text-cyan-400 focus:ring-cyan-400/50 focus:ring-offset-0 cursor-pointer"
                />
                <span className="group-hover:underline">{LOGIN_TEXT.rememberMe}</span>
              </label>
              <a 
                href="/#/forgot-password" 
                className="text-cyan-300 hover:text-cyan-200 transition-colors hover:underline"
              >
                {LOGIN_TEXT.forgotPassword}
              </a>
            </div>

            {/* Login Button - Matching Logo Icon Color */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 px-6 rounded-2xl font-semibold text-white bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-cyan-500/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {LOGIN_TEXT.signingInButton}
                </div>
              ) : (
                LOGIN_TEXT.signInButton
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="mt-8 mb-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/20"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-transparent text-white/60">{LOGIN_TEXT.newUser}</span>
              </div>
            </div>
          </div>

          {/* Sign Up Link */}
          <div className="text-center">
            <a 
              href="/#/signup" 
              className="inline-block px-8 py-3 rounded-2xl font-semibold text-white border-2 border-white/30 hover:border-white/50 hover:bg-white/10 transform hover:scale-105 transition-all duration-200"
            >
              {LOGIN_TEXT.createAccount}
            </a>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center text-white/60 text-sm">
          <p>
            {LOGIN_TEXT.termsText}{' '}
            <a href="#" className="text-white/80 hover:text-white underline">{LOGIN_TEXT.termsLink}</a>
            {' '}{LOGIN_TEXT.and}{' '}
            <a href="#" className="text-white/80 hover:text-white underline">{LOGIN_TEXT.privacyLink}</a>
          </p>
        </div>
      </div>

      {/* Custom Animations */}
      <style>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(20px); }
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-gradient {
          animation: gradient 15s ease infinite;
          background-size: 200% 200%;
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 8s ease-in-out infinite;
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default LoginGlass;
